import { Button } from '@/components/ui/button';
import { ArrowRight, Zap, Brain, Cpu, Shield, Network, Database, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { DemoButton } from '@/components/DemoCTA';
import techHeroImage from '@/assets/tech-hero-bg.jpg';

const Hero = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<any[]>([]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Neural Network Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create neural network nodes
    const createNodes = () => {
      const nodes = [];
      const nodeCount = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));
      
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 1,
          connections: [],
          pulse: Math.random() * Math.PI * 2,
        });
      }
      return nodes;
    };

    nodesRef.current = createNodes();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update nodes
      nodesRef.current.forEach((node, i) => {
        // Update position
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += 0.02;

        // Boundary collision
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        
        // Keep in bounds
        node.x = Math.max(0, Math.min(canvas.width, node.x));
        node.y = Math.max(0, Math.min(canvas.height, node.y));

        // Friction
        node.vx *= 0.99;
        node.vy *= 0.99;

        // Find connections
        node.connections = [];
        nodesRef.current.forEach((otherNode, j) => {
          if (i !== j) {
            const dist = Math.sqrt(
              Math.pow(node.x - otherNode.x, 2) + Math.pow(node.y - otherNode.y, 2)
            );
            if (dist < 120) {
              node.connections.push({ node: otherNode, distance: dist });
            }
          }
        });
      });

      // Draw connections
      nodesRef.current.forEach(node => {
        node.connections.forEach(({ node: connectedNode, distance }) => {
          const opacity = Math.max(0, (120 - distance) / 120) * 0.5;
          const gradient = ctx.createLinearGradient(node.x, node.y, connectedNode.x, connectedNode.y);
          gradient.addColorStop(0, `hsla(180, 100%, 50%, ${opacity})`);
          gradient.addColorStop(0.5, `hsla(300, 100%, 70%, ${opacity * 1.5})`);
          gradient.addColorStop(1, `hsla(180, 100%, 50%, ${opacity})`);
          
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(connectedNode.x, connectedNode.y);
          ctx.stroke();
        });
      });

      // Draw nodes
      nodesRef.current.forEach(node => {
        const pulse = Math.sin(node.pulse) * 0.5 + 0.5;
        const size = node.size + pulse * 2;
        
        // Node glow
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, size * 3);
        gradient.addColorStop(0, `hsla(180, 100%, 50%, ${0.8 + pulse * 0.2})`);
        gradient.addColorStop(0.3, `hsla(300, 100%, 70%, ${0.4 + pulse * 0.2})`);
        gradient.addColorStop(1, 'hsla(180, 100%, 50%, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, size * 3, 0, Math.PI * 2);
        ctx.fill();

        // Node core
        ctx.fillStyle = `hsla(180, 100%, 50%, ${0.9 + pulse * 0.1})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, size, 0, Math.PI * 2);
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);


  // Restrained floating elements — only 2, far from center to keep focus on copy
  const floatingElements = [
    { icon: Shield, delay: '1s', position: 'top-28 right-10 hidden xl:block', color: 'text-accent' },
    { icon: Network, delay: '2s', position: 'bottom-32 left-10 hidden xl:block', color: 'text-primary' },
  ];

  const trustLogos = ['Fortune 500', 'ISO 27001', 'SOC 2 Type II', 'GDPR Ready', 'Enterprise SLA'];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero">
      {/* Dynamic Neural Network Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ background: 'transparent' }}
      />
      
      {/* Animated Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{ backgroundImage: `url(${techHeroImage})` }}
      />
      
      {/* Neural Network Grid Pattern */}
      <div className="absolute inset-0 bg-pattern-grid opacity-10" style={{ backgroundSize: '50px 50px' }} />
      
      {/* Floating Tech Elements */}
      {floatingElements.map((element, index) => {
        const IconComponent = element.icon;
        return (
          <div
            key={index}
            className={`absolute ${element.position} opacity-20 animate-float`}
            style={{ animationDelay: element.delay }}
          >
            <div className="p-4 rounded-full bg-gradient-surface backdrop-blur-sm border border-primary/20">
              <IconComponent className={`h-8 w-8 ${element.color}`} />
            </div>
          </div>
        );
      })}

      {/* Glowing Orbs */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary/20 rounded-full blur-xl animate-glow-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-accent/20 rounded-full blur-xl animate-glow-pulse" style={{ animationDelay: '1s' }} />
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 text-center">
        <div className="max-w-5xl mx-auto">
          {/* Badge — restrained, with live indicator */}
          <div
            className={`inline-flex items-center gap-2 bg-card/40 backdrop-blur-md border border-primary/20 rounded-full px-4 py-2 mb-10 transform transition-all duration-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
            <span className="text-xs font-medium text-foreground/90 tracking-wide uppercase">{t('hero.badge')}</span>
          </div>

          {/* Main Heading — single gradient phrase */}
          <h1 className={`text-5xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight leading-[1.05] transform transition-all duration-1000 delay-200 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>
            <span className="text-foreground">Enterprise AI,</span>
            <br />
            <span className="bg-gradient-tech bg-clip-text text-transparent">engineered to scale.</span>
          </h1>

          {/* Single clean subline */}
          <p className={`text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed transform transition-all duration-1000 delay-300 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>
            Autonomous agents, secure infrastructure, and quantum-ready architecture for organizations that won't compromise.
          </p>

          {/* Primary CTAs — exactly 2 */}
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 mb-6 transform transition-all duration-1000 delay-500 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>
            <DemoButton size="lg" className="min-w-[200px]" label="Request a Demo" />
            <Link to="/assessment" className="block">
              <Button variant="outline" size="lg" className="min-w-[200px] h-12 text-base">
                Take Free Assessment
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Secondary actions — text links */}
          <div className={`flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-16 text-sm transform transition-all duration-1000 delay-600 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>
            <Link to="/services" className="text-muted-foreground hover:text-primary transition-tech inline-flex items-center gap-1">
              Explore Services <ArrowRight className="h-3 w-3" />
            </Link>
            <span className="text-border">·</span>
            <Link to="/rpa-assessment" className="text-muted-foreground hover:text-primary transition-tech inline-flex items-center gap-1">
              Automation Audit <ArrowRight className="h-3 w-3" />
            </Link>
            <span className="text-border">·</span>
            <Link to="/sovereign-scout" className="text-muted-foreground hover:text-primary transition-tech inline-flex items-center gap-1">
              Sovereign Scout <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Trust strip */}
          <div className={`mb-14 transform transition-all duration-1000 delay-700 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70 mb-4">
              Built to enterprise standards
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {trustLogos.map((logo) => (
                <div key={logo} className="text-xs font-semibold text-muted-foreground/60 tracking-wider uppercase">
                  {logo}
                </div>
              ))}
            </div>
          </div>

          {/* Metrics — restrained, monochrome */}
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 max-w-4xl mx-auto pt-10 border-t border-border/40 transform transition-all duration-1000 delay-900 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>
            {[
              { value: '99.9%', label: 'Uptime SLA', icon: Brain },
              { value: '10×', label: 'Faster processing', icon: Zap },
              { value: '256-bit', label: 'Encryption', icon: Shield },
              { value: '24/7', label: 'Autonomous ops', icon: Cpu },
            ].map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="text-center">
                  <Icon className="h-5 w-5 text-primary/70 mx-auto mb-3" />
                  <div className="text-3xl md:text-4xl font-bold text-foreground mb-1 tracking-tight">{stat.value}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Subtle bottom fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;